import {
	type ClipboardEvent,
	type KeyboardEvent,
	useEffect,
	useRef,
	useState,
} from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { AdminIcon } from "@/components/admin/icon";
import { AuthBrand } from "@/components/auth/auth-brand";
import {
	extractErrorMessage,
	useVerify2faMutation,
} from "@/hooks/use-auth-api";
import { useAuthStore } from "@/stores/auth.store";

export function TwoFactorPage() {
	const pending = useAuthStore((s) => s.pending2FA);
	const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
	const setSession = useAuthStore((s) => s.setSession);
	const cancelPending = useAuthStore((s) => s.cancelPending);
	const navigate = useNavigate();

	const [code, setCode] = useState<string[]>(["", "", "", "", "", ""]);
	const [error, setError] = useState<string | null>(null);
	const [seconds, setSeconds] = useState(58);
	const refs = useRef<(HTMLInputElement | null)[]>([]);
	const verifyMutation = useVerify2faMutation();

	useEffect(() => {
		refs.current[0]?.focus();
		const id = setInterval(() => setSeconds((s) => Math.max(0, s - 1)), 1000);
		return () => clearInterval(id);
	}, []);

	if (isAuthenticated) return <Navigate to="/" replace />;
	if (!pending || !pending.token) return <Navigate to="/login" replace />;

	const completeLogin = async (full: string) => {
		setError(null);
		try {
			const res = await verifyMutation.mutateAsync({
				pendingToken: pending.token,
				code: full,
			});
			if (
				res.status === "authenticated" &&
				res.user &&
				res.accessToken &&
				res.refreshToken
			) {
				setSession({
					user: res.user,
					accessToken: res.accessToken,
					refreshToken: res.refreshToken,
				});
				navigate("/");
			} else {
				setError("Réponse inattendue du serveur");
			}
		} catch (err) {
			setError(extractErrorMessage(err, "Code incorrect"));
			setCode(["", "", "", "", "", ""]);
			refs.current[0]?.focus();
		}
	};

	const setDigit = (i: number, v: string) => {
		const d = v.replace(/\D/g, "").slice(-1);
		const nc = [...code];
		nc[i] = d;
		setCode(nc);
		if (error) setError(null);
		if (d && i < 5) refs.current[i + 1]?.focus();
		if (nc.every((x) => x) && i === 5) void completeLogin(nc.join(""));
	};

	const onKey = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Backspace" && !code[i] && i > 0)
			refs.current[i - 1]?.focus();
	};

	const onPaste = (e: ClipboardEvent<HTMLDivElement>) => {
		const v = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
		if (v.length === 6) {
			e.preventDefault();
			setCode(v.split(""));
			refs.current[5]?.focus();
			void completeLogin(v);
		}
	};

	const allFilled = code.every((x) => x);
	const defaultMethod =
		pending.methods.find((m) => m.isDefault) ?? pending.methods[0];
	const channelLabel =
		defaultMethod?.type === "totp"
			? "Application authenticator"
			: defaultMethod?.type === "sms"
				? "SMS"
				: defaultMethod?.type === "email"
					? "Email"
					: defaultMethod?.type === "whatsapp"
						? "WhatsApp Business"
						: "Code de vérification";

	return (
		<div className="auth-page">
			<AuthBrand />
			<main className="auth-panel">
				<div className="auth-form-wrap">
					<div className="auth-stepper">
						<div className="dot done">
							<AdminIcon n="check" s={10} c="#fff" />
						</div>
						<div className="seg-line done" />
						<div className="dot active">2</div>
						<span className="lbl">Étape 2 sur 2 · Code de vérification</span>
					</div>

					<div className="auth-h">
						<h2>Saisissez votre code de vérification</h2>
						<p>
							Code à 6 chiffres reçu via votre méthode 2FA. Valide 30 secondes.
						</p>
					</div>

					{error && (
						<div
							className="auth-alert danger"
							role="alert"
							data-testid="twofa-error"
						>
							<span className="icon">
								<AdminIcon n="alert" s={16} />
							</span>
							<div>
								<strong>Code incorrect</strong>
								{error}
							</div>
						</div>
					)}

					<div className="otp-channel">
						<div className="icon">
							<AdminIcon n="qr" s={18} />
						</div>
						<div>
							<div className="label">Source</div>
							<div className="target">{channelLabel}</div>
						</div>
					</div>

					<div className="otp-row" onPaste={onPaste} data-testid="twofa-row">
						{code.map((v, i) => (
							<input
								key={i}
								ref={(el) => {
									refs.current[i] = el;
								}}
								className={
									"otp-input " + (v ? "filled" : "") + (error ? "error" : "")
								}
								type="text"
								inputMode="numeric"
								maxLength={1}
								value={v}
								onChange={(e) => setDigit(i, e.target.value)}
								onKeyDown={(e) => onKey(i, e)}
								data-testid={`twofa-digit-${i}`}
							/>
						))}
					</div>

					<div className="otp-status">
						{seconds > 0 ? (
							<>
								Nouveau code disponible dans{" "}
								<span className="timer">
									0:{String(seconds).padStart(2, "0")}
								</span>
							</>
						) : (
							<a className="resend" onClick={() => setSeconds(58)}>
								Renvoyer le code
							</a>
						)}
					</div>

					<label className="checkbox" style={{ marginBottom: 18 }}>
						<input type="checkbox" />
						<span>
							Faire confiance à cet appareil 30 jours (réseau interne
							uniquement)
						</span>
					</label>

					<button
						className="btn-submit"
						onClick={() => void completeLogin(code.join(""))}
						disabled={!allFilled || verifyMutation.isPending}
						type="button"
						data-testid="twofa-submit"
					>
						{verifyMutation.isPending ? "Vérification…" : "Vérifier le code"}
						<AdminIcon n="check" s={16} c="#fff" />
					</button>
					<button
						type="button"
						className="btn-auth-ghost"
						style={{ marginTop: 8 }}
						onClick={() => {
							cancelPending();
							navigate("/login");
						}}
					>
						<AdminIcon n="chevL" s={14} /> Retour
					</button>

					<div className="security-card">
						<span className="icon">
							<AdminIcon n="info" s={16} />
						</span>
						<div>
							<strong>Pour information</strong>
							<span>
								Le code TOTP change toutes les 30 secondes. Si vous voyez le
								timer rafraîchir dans l'app, attendez le code suivant pour
								éviter une expiration en cours de saisie.
							</span>
						</div>
					</div>
				</div>
			</main>
		</div>
	);
}
